import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { CheckCircle2, ArrowLeft, Loader2, Sparkles, Coins, Zap, ShieldCheck, Info } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL;

export default function PricingPage() {
    const navigate = useNavigate();
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    // 'recurring' = Abonelik, 'one-time' = Tek Seferlik
    const [billingType, setBillingType] = useState('recurring'); 

    useEffect(() => {
        const fetchPlans = async () => {
            try {
                // Public endpoint'e istek atıyoruz (Token gerektirmez)
                const response = await axios.get(`${API_URL}/api/plans`);
                setPlans(response.data);
            } catch (error) {
                console.error("Planlar yüklenemedi:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchPlans();
    }, []);

    // Planları kullanıcının seçtiği türe göre filtrele
    const filteredPlans = plans.filter(p => 
        billingType === 'recurring' ? p.is_recurring : !p.is_recurring
    );

    // Satın al butonuna basılınca
    const handleBuyClick = (plan) => {
        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/login');
            return;
        }
        // YENİ EKLENEN SATIR: Kullanıcıyı Checkout sayfasına plan ID'si ile yönlendir
        navigate(`/checkout/${plan.id}`); 
    };

    // Animasyon Varyantları
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
    };

    const cardVariants = {
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center text-white">
                <Loader2 className="animate-spin w-12 h-12 text-indigo-500 mb-4" />
                <p className="text-slate-400 font-medium tracking-widest uppercase text-sm">Paketler Yükleniyor...</p>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-950 text-slate-200 font-sans relative overflow-hidden pb-24">
            {/* Arka Plan Efektleri */}
            <div className="absolute top-0 inset-x-0 h-full w-full overflow-hidden pointer-events-none z-0">
                <div className="absolute top-[-10%] left-[20%] w-[40%] h-[30%] bg-indigo-600/20 rounded-full blur-[120px]" />
                <div className="absolute top-[20%] right-[-10%] w-[30%] h-[40%] bg-purple-600/10 rounded-full blur-[120px]" />
            </div>

            <div className="relative z-10 max-w-7xl mx-auto px-6 pt-8">
                {/* Geri Dön Butonu */}
                <button 
                    onClick={() => navigate(-1)} 
                    className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors bg-slate-900/50 hover:bg-slate-800 px-4 py-2 rounded-full border border-slate-800 w-fit mb-12"
                >
                    <ArrowLeft size={18} /> Geri Dön
                </button>

                {/* Başlık Alanı */}
                <div className="text-center max-w-3xl mx-auto mb-16">
                    <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="inline-flex items-center gap-2 bg-indigo-500/10 border border-indigo-500/20 text-indigo-400 px-4 py-1.5 rounded-full text-sm font-bold uppercase tracking-widest mb-6">
                        <Sparkles size={16} /> Mekânname Fiyatlandırma
                    </motion.div>
                    <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="text-4xl md:text-6xl font-black text-white mb-6 tracking-tight">
                        Tarihi Keşfetmek İçin <br/> <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-cyan-400">Gücünü Seç</span>
                    </motion.h1>
                    <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="text-lg text-slate-400 leading-relaxed">
                        İster aylık abone olarak tüm özellikleri limitsiz hissiyle kullan, ister sadece ihtiyacın kadar token yükle. Kontrol tamamen sende.
                    </motion.p>
                </div>

                {/* Switch (Abonelik / Tek Seferlik) & KDV Notu */}
                <motion.div 
                    initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
                    className="flex flex-col items-center justify-center mb-16"
                >
                    <div className="bg-slate-900 p-1.5 rounded-2xl border border-slate-800 flex relative z-20 shadow-xl mb-4">
                        <button
                            onClick={() => setBillingType('recurring')}
                            className={`relative px-8 py-3 rounded-xl font-bold text-sm md:text-base transition-all duration-300 z-10 
                            ${billingType === 'recurring' ? 'text-white' : 'text-slate-400 hover:text-slate-200'}`}
                        >
                            {billingType === 'recurring' && (
                                <motion.div layoutId="billing-pill" className="absolute inset-0 bg-indigo-600 rounded-xl shadow-lg -z-10"></motion.div>
                            )}
                            Aylık Abonelik
                        </button>
                        <button
                            onClick={() => setBillingType('one-time')}
                            className={`relative px-8 py-3 rounded-xl font-bold text-sm md:text-base transition-all duration-300 z-10 
                            ${billingType === 'one-time' ? 'text-white' : 'text-slate-400 hover:text-slate-200'}`}
                        >
                            {billingType === 'one-time' && (
                                <motion.div layoutId="billing-pill" className="absolute inset-0 bg-indigo-600 rounded-xl shadow-lg -z-10"></motion.div>
                            )}
                            Tek Seferlik Yükleme
                        </button>
                    </div>
                    
                    {/* KDV Notu Alanı */}
                    <div className="flex items-center gap-2 text-slate-500 text-sm font-medium bg-slate-900/50 px-4 py-2 rounded-full border border-slate-800">
                        <Info size={16} className="text-indigo-400" />
                        <span>Fiyatlarımıza KDV dahil değildir.</span>
                    </div>
                </motion.div>

                {/* Paket Grid'i */}
                {filteredPlans.length > 0 ? (
                    <motion.div 
                        variants={containerVariants}
                        initial="hidden"
                        animate="visible"
                        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto"
                    >
                        {filteredPlans.map((plan, index) => {
                            // Ortadaki planı (veya en pahalı olmayanı) vurgulamak için bir mantık
                            const isPopular = filteredPlans.length >= 3 ? index === 1 : index === 0;

                            return (
                                <motion.div 
                                    key={plan.id}
                                    variants={cardVariants}
                                    className={`relative rounded-[2.5rem] p-8 flex flex-col transition-all duration-300 
                                    ${isPopular 
                                        ? 'bg-gradient-to-b from-indigo-900/50 to-slate-900 border-2 border-indigo-500 shadow-2xl shadow-indigo-500/20 md:-translate-y-4' 
                                        : 'bg-slate-900/50 border border-slate-800 hover:border-slate-600 hover:bg-slate-800'}`}
                                >
                                    {isPopular && (
                                        <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-indigo-500 to-cyan-400 text-white font-black text-xs uppercase tracking-widest py-1.5 px-4 rounded-full shadow-lg">
                                            En Çok Tercih Edilen
                                        </div>
                                    )}

                                    <div className="mb-8">
                                        <h3 className="text-2xl font-black text-white mb-2">{plan.name}</h3>
                                        {/* Fiyat */}
                                        <div className="flex items-end gap-1 mb-4">
                                            <span className="text-5xl font-extrabold text-white">₺{plan.price.toFixed(2)}</span>
                                            {plan.is_recurring && <span className="text-slate-400 font-medium mb-1">/ ay</span>}
                                        </div>
                                        {/* Token Göstergesi */}
                                        <div className="inline-flex items-center gap-2 bg-amber-500/10 border border-amber-500/20 text-amber-400 px-4 py-2 rounded-xl">
                                            <Coins size={20} />
                                            <span className="font-bold text-lg">{plan.token_amount} Token</span>
                                        </div>
                                    </div>

                                    {/* Açıklama / Özellik Listesi */}
                                    <div className="flex-1">
                                        <p className="text-slate-400 text-sm leading-relaxed mb-6 border-b border-slate-800 pb-6">
                                            {plan.description || "Bu paket ile Mekânname'nin eşsiz AI özelliklerini kullanabilirsiniz."}
                                        </p>
                                        
                                        <ul className="space-y-4 mb-8">
                                            <li className="flex items-start gap-3">
                                                <CheckCircle2 size={18} className={isPopular ? "text-indigo-400" : "text-emerald-500"} />
                                                <span className="text-sm font-medium text-slate-300">Detaylı Fotoğraf Analizi</span>
                                            </li>
                                            <li className="flex items-start gap-3">
                                                <CheckCircle2 size={18} className={isPopular ? "text-indigo-400" : "text-emerald-500"} />
                                                <span className="text-sm font-medium text-slate-300">Kişiselleştirilmiş Gezi Rotaları</span>
                                            </li>
                                            <li className="flex items-start gap-3">
                                                <CheckCircle2 size={18} className={isPopular ? "text-indigo-400" : "text-emerald-500"} />
                                                <span className="text-sm font-medium text-slate-300">AI Sesli Hikaye Anlatımı</span>
                                            </li>
                                            {/*isPopular && (
                                                <li className="flex items-start gap-3">
                                                    <Zap size={18} className="text-amber-400" />
                                                    <span className="text-sm font-bold text-white">Öncelikli Sunucu Erişimi</span>
                                                </li>
                                            )*/}
                                        </ul>
                                    </div>

                                    {/* Buton */}
                                    <button 
                                        onClick={() => handleBuyClick(plan)}
                                        className={`w-full py-4 rounded-xl font-bold transition-all active:scale-95 flex items-center justify-center gap-2
                                        ${isPopular 
                                            ? 'bg-indigo-600 hover:bg-indigo-500 text-white shadow-lg shadow-indigo-600/30' 
                                            : 'bg-slate-800 hover:bg-slate-700 text-white border border-slate-700 hover:border-slate-600'}`}
                                    >
                                        <ShieldCheck size={18} />
                                        {plan.price === 0 ? "Ücretsiz Başla" : "Hemen Satın Al"}
                                    </button>
                                </motion.div>
                            );
                        })}
                    </motion.div>
                ) : (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-20 bg-slate-900/50 rounded-3xl border border-slate-800 max-w-3xl mx-auto">
                        <Coins className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                        <h3 className="text-xl font-bold text-white mb-2">Paket Bulunamadı</h3>
                        <p className="text-slate-400">Şu anda bu kategoriye ait aktif bir paket bulunmuyor.</p>
                    </motion.div>
                )}

                {/* SSS veya Güvenlik İbaresi */}
                <div className="mt-24 text-center pb-12 border-t border-slate-800 pt-12">
                    <div className="flex items-center justify-center gap-2 text-slate-400 mb-2">
                        <ShieldCheck size={20} className="text-emerald-500" />
                        <span className="font-medium">256-bit SSL Güvencesi</span>
                    </div>
                    <p className="text-xs text-slate-500">Tüm ödemeleriniz güvenli altyapılar üzerinden şifrelenerek gerçekleştirilir. Kredi kartı bilgileriniz sunucularımızda saklanmaz.</p>
                </div>

            </div>
        </div>
    );
}